import { useState } from "react";
import { Pin, X, Play } from "lucide-react";
import { toast } from "sonner";
import { addChapter, removeChapter } from "../../lib/api";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const ChapterList = ({ chapters, onSeek, onRemove, current }) => (
  <ol className="grid gap-1" data-testid="chapter-list">
    {chapters.map((c) => (
      <li key={c.id} className={`flex items-center gap-2 rounded-xl border px-3 py-1.5 text-sm ${current === c.id ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`chapter-${c.id}`}>
        <button type="button" onClick={() => onSeek(c.time)} className="flex-1 min-w-0 flex items-center gap-2 text-left hover:text-primary" data-testid={`chapter-play-${c.id}`}>
          <Play className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="font-mono text-[11px] text-muted-foreground shrink-0">{fmtTime(c.time)}</span>
          <span className="truncate">{c.label}</span>
        </button>
        {onRemove && <button type="button" onClick={() => onRemove(c.id)} className="text-muted-foreground hover:text-destructive" aria-label="Remove pin" data-testid={`chapter-remove-${c.id}`}><X className="h-3.5 w-3.5" /></button>}
      </li>
    ))}
  </ol>
);

// Pin favourite moments ("the kitchen table story") so family can jump straight to them.
export const LetterChapters = ({ doc, mediaRef, readOnly, onUpdated, segments = [] }) => {
  const [label, setLabel] = useState("");
  const [at, setAt] = useState(null);
  const chapters = doc?.chapters || [];
  const seek = (t) => { const el = mediaRef.current; if (el) { el.currentTime = t; el.play?.().catch(() => {}); } };
  const suggest = (t) => {
    const seg = segments.find((s) => t >= s.start && t < Math.max(s.end, s.start + 0.5)) || segments.find((s) => s.start >= t);
    const words = (seg?.text || "").replace(/[.!?,;:]+$/, "").trim();
    return words.length > 60 ? `${words.slice(0, 57).replace(/\s+\S*$/, "")}…` : words;
  };
  const begin = () => { const el = mediaRef.current; el?.pause?.(); const t = Math.floor((el?.currentTime || 0) * 10) / 10; setAt(t); setLabel(suggest(t)); };
  const save = async () => {
    if (!label.trim()) return;
    try { onUpdated?.(await addChapter(doc.id, { time: at, label: label.trim() })); setLabel(""); setAt(null); toast.success("Moment pinned"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't pin that moment"); }
  };
  const remove = async (id) => { try { onUpdated?.(await removeChapter(doc.id, id)); } catch { toast.error("Couldn't remove the pin"); } };
  if (readOnly && !chapters.length) return null;
  return (
    <div className="mt-3" data-testid="letter-chapters">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5"><Pin className="h-3.5 w-3.5" /> Favourite moments{chapters.length ? ` · ${chapters.length}` : ""}</div>
        {!readOnly && at === null && <button type="button" onClick={begin} className="text-xs text-primary hover:underline" data-testid="chapter-pin-btn">Pin this moment</button>}
      </div>
      {at !== null && (
        <form onSubmit={(e) => { e.preventDefault(); save(); }} className="mb-2 flex items-center gap-2" data-testid="chapter-form">
          <span className="font-mono text-xs text-muted-foreground shrink-0">{fmtTime(at)}</span>
          <Input autoFocus value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} placeholder="What happens here? e.g. the kitchen table story" title="Pre-filled from the captions — edit freely" className="h-8 text-sm" data-testid="chapter-label-input" />
          <Button type="submit" size="sm" className="rounded-full h-8" disabled={!label.trim()} data-testid="chapter-save-btn">Pin</Button>
          <Button type="button" size="sm" variant="ghost" className="h-8" onClick={() => { setAt(null); setLabel(""); }} data-testid="chapter-cancel-btn">Cancel</Button>
        </form>
      )}
      {chapters.length ? <ChapterList chapters={chapters} onSeek={seek} onRemove={readOnly ? null : remove} /> : !readOnly && at === null && (
        <p className="text-xs text-muted-foreground" data-testid="chapter-empty">Play to a favourite part, then pin it so family can jump straight there.</p>
      )}
    </div>
  );
};
